import { hourOf } from '../state/rules';
import { usedToday, useToday } from '../state/daily';
import type { Interactions } from '../systems/Interactions';
import { MUD_BOG, mudAt, SPOTS } from '../world/layout';
import { RODEO_ENTRY, RODEO_PURSE, SHELLS_PRICE, type Games } from './Games';
import type { Driving } from './Driving';
import type { GameServices } from './services';

export const MUD_ENTRY = 10;
export const MUD_RUNS_PER_DAY = 3;
/** Finish under these times (seconds) to get paid. */
export const MUD_PAYOUTS: ReadonlyArray<[number, number]> = [
  [14, 60],
  [20, 30],
  [28, 12],
];

const START_X = MUD_BOG.minX + 1.5;
const FINISH_X = MUD_BOG.maxX - 1.5;

/** Payout for a mud run time (0 if too slow). */
export function mudPayout(seconds: number): number {
  for (const [t, pay] of MUD_PAYOUTS) if (seconds < t) return pay;
  return 0;
}

/**
 * The fairgrounds: ride Widowmaker at the rodeo, time yourself through the mud bog,
 * and plink cans at Bubba's range.
 */
export class Fairgrounds {
  /** A paid run waiting to start, then the clock while it's running. */
  private armedUntil = -1;
  private runStart = -1;
  /** Made it to the far arch; now get back. */
  private turned = false;
  private lastX = 0;
  private splash = 0;

  constructor(
    private readonly s: GameServices,
    private readonly driving: Driving,
    private readonly games: Games,
  ) {}

  /** For the objective pill while a run is on. */
  get runText(): string | null {
    if (this.runStart >= 0)
      return `:stopwatch: MUD RUN ${(this.s.now() - this.runStart).toFixed(1)}s · ${this.turned ? 'back through the START arch!' : 'around the far arch and back'}`;
    if (this.armedUntil > this.s.now())
      return ':checkered-flag: Mud run paid: drive east through the START arch';
    return null;
  }

  private rodeoOpen(): boolean {
    const h = hourOf(this.s.store.minutes);
    return h >= 16 && h < 23;
  }

  register(interactions: Interactions): void {
    const { store } = this.s;
    const add = interactions.add.bind(interactions);
    add({
      id: 'rodeoChute',
      at: () => SPOTS.rodeoChute,
      radius: 1.8,
      prompt: () =>
        this.rodeoOpen()
          ? `Ride Widowmaker ($${RODEO_ENTRY} entry, $${RODEO_PURSE} purse)`
          : 'The bucking chute (rodeo 4 PM to 11 PM)',
      action: () => {
        if (!this.rodeoOpen()) {
          store.toast("Widowmaker's asleep. Rodeo's 4 to 11. Don't wake him. Seriously.", 'info', ':bull:');
          return;
        }
        this.games.play('rodeo', RODEO_ENTRY);
      },
    });
    add({
      id: 'mudSign',
      at: () => SPOTS.mudSign,
      radius: 2.2,
      prompt: () => {
        if (this.armedUntil > this.s.now() || this.runStart >= 0) return null;
        const left = MUD_RUNS_PER_DAY - usedToday(store, 'mudrun');
        return left > 0
          ? `Pay for a timed mud run ($${MUD_ENTRY}, ${left} left today)`
          : 'Mud runs (done for today)';
      },
      action: () => {
        if (usedToday(store, 'mudrun') >= MUD_RUNS_PER_DAY) {
          store.toast('Three timed runs a day. The judges need to hose off.', 'info', ':checkered-flag:');
          return;
        }
        if (!store.spend(MUD_ENTRY)) {
          store.toast(`$${MUD_ENTRY} to run. Mud ain't free.`, 'bad', ':checkered-flag:');
          return;
        }
        useToday(store, 'mudrun');
        this.armedUntil = this.s.now() + 180;
        store.toast(
          `Paid! Drive east through the START arch, around the far arch and back. Under ${MUD_PAYOUTS[0]?.[0]}s pays $${MUD_PAYOUTS[0]?.[1]}. Big tires help.`,
          'good',
          ':checkered-flag:',
        );
      },
    });
    add({
      id: 'gunCounter',
      at: () => SPOTS.gunCounter,
      radius: 1.8,
      prompt: () => {
        const h = hourOf(store.minutes);
        return h >= 8 && h < 19
          ? `Rent a lane and a rifle ($${SHELLS_PRICE}, 12 shells)`
          : 'Gun range (open 8 AM to 7 PM)';
      },
      action: () => {
        const h = hourOf(store.minutes);
        if (h < 8 || h >= 19) {
          store.toast(
            'Range is closed. Gus does not shoot in the dark and neither do you.',
            'info',
            ':target:',
          );
          return;
        }
        this.games.play('shoot', SHELLS_PRICE);
      },
    });
  }

  update(dt: number): void {
    const { store, world, ui, player } = this.s;
    const now = this.s.now();
    // The bull in the pen paws the dirt.
    const bull = world.fair.bull;
    if (!bull.busy) {
      bull.head.rotation.z = Math.sin(now * 1.3) * 0.15;
      bull.group.rotation.y = Math.sin(now * 0.4) * 0.3;
    }

    if (!this.driving.active) {
      if (this.runStart >= 0) {
        this.runStart = -1;
        store.toast(
          'You got out. Run disqualified. The judges are disappointed. The mud is not.',
          'bad',
          ':checkered-flag:',
        );
      }
      return;
    }
    const p = this.driving.position;
    const mud = mudAt(p.x, p.z);
    // Mud flies when you spin your tires in it.
    this.splash -= dt;
    if (mud > 0.2 && Math.abs(this.driving.car.speed) > 2 && this.splash <= 0) {
      this.splash = 0.05;
      const f = this.driving.car.forward(player.position.clone());
      world.smoke.spawn({
        x: p.x - f.x * 2.2,
        y: 0.4,
        z: p.z - f.z * 2.2,
        vx: (Math.random() - 0.5) * 3 - f.x * 3,
        vy: 2.5 + Math.random() * 2,
        vz: (Math.random() - 0.5) * 3 - f.z * 3,
        life: 0.9,
        size: 0.5,
        grow: 1,
        color: '#4a3826',
      });
      if (!store.flag('first_mud')) {
        store.setFlag('first_mud', true);
        store.toast('MUD! Your ride is now 40% bog. This is the way.', 'meme', ':mud-splat:');
      }
    }
    const inLane = p.z > MUD_BOG.minZ + 1 && p.z < MUD_BOG.maxZ - 1;
    const crossed = (x: number): boolean => this.lastX < x && p.x >= x;
    if (this.armedUntil > now && inLane && crossed(START_X)) {
      this.armedUntil = -1;
      this.runStart = now;
      this.turned = false;
      this.s.sfx('airhorn');
    }
    if (this.runStart >= 0 && !this.turned && p.x >= FINISH_X) {
      this.turned = true;
      this.s.sfx('horn');
    }
    const back = this.lastX > START_X && p.x <= START_X;
    if (this.runStart >= 0 && this.turned && inLane && back) {
      const time = now - this.runStart;
      this.runStart = -1;
      store.incr('mudRuns');
      const pay = mudPayout(time);
      const best = Number(store.flag('mud_best') ?? 0);
      if (!best || time < best) store.setFlag('mud_best', Math.round(time * 10) / 10);
      if (pay) store.earn(pay);
      store.addClout(pay ? 6 : 2);
      if (time < 14) store.unlock('mud_king');
      this.s.sfx(pay ? 'cash' : 'fail');
      ui.hud.float(
        `:checkered-flag: ${time.toFixed(1)}s`,
        player.headPosition(),
        pay ? '#9dff6a' : '#ffffff',
      );
      store.toast(
        pay
          ? `${time.toFixed(1)} seconds through the bog! The judges hand you $${pay} and a towel that makes it worse.`
          : `${time.toFixed(1)} seconds. Too slow to pay, but the crowd liked the part where you went sideways.`,
        pay ? 'good' : 'info',
        ':checkered-flag:',
      );
      this.s.share({ kind: 'stunt', ok: true, name: `Mud Run ${time.toFixed(1)}s` });
    }
    this.lastX = p.x;
  }
}
